import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type { AddressChange, AddressCheckSummary, GeoReviewItem } from '../api/types';
import { ErrorMessage } from '../components/ErrorMessage';
import { PageLoader } from '../components/Loader';
import { Modal } from '../components/Modal';
import { MutationError } from '../components/MutationError';
import { GoogleAttribution } from '../places/GoogleAttribution';
import { mapsSearchUrl } from '../places/maps';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { isAssumed, numberFormat } from './labels';

// Google-Adressprüfung der Geo-Klärung: Das System prüft offene Fälle bei Google (Address Validation API) und zeigt die
// Vorschläge. Übernommen wird nur, was ein Mitarbeiter bestätigt – einzeln im Fall oder gesammelt nach Durchsicht
// (Nutzungsbedingungen von Google); nicht übernommene Ergebnisse löscht das System nach 29 Tagen.

const fieldLabels: Record<string, string> = { street: 'Straße', house_number: 'Hausnr.', postal_code: 'PLZ', city: 'Ort' };

// MapsLink: Adresse in Google Maps gegenprüfen (neuer Tab).
function MapsLink({ address, label = 'In Google Maps ansehen' }: { address: string; label?: string }) {
  return (
    <a className="maps-link" href={mapsSearchUrl(address)} target="_blank" rel="noopener noreferrer">
      {label}
    </a>
  );
}

function Changes({ changes }: { changes: AddressChange[] }) {
  return (
    <ul className="plain-list">
      {changes.map((c) => (
        <li key={c.field}>
          {fieldLabels[c.field] ?? c.field}: <s>{c.from || '–'}</s> → <strong>{c.to}</strong>
        </li>
      ))}
    </ul>
  );
}

// AddressCheckBar: Stand der Prüfung, Start und Sammelbestätigung der Korrekturen.
export function AddressCheckBar({ summary, checking, onStarted }: { summary: AddressCheckSummary; checking: boolean; onStarted: () => void }) {
  const papi = useProjectApi();
  const [open, setOpen] = useState(false);
  const start = useMutation({ mutationFn: () => papi.startAddressChecks(), onSuccess: onStarted });
  const s = summary.statuses;
  const corrected = s.CORRECTED ?? 0;
  const reviewable = summary.with_changes;
  const running = checking && summary.pending > 0;
  const parts = [
    summary.pending > 0 && `${numberFormat.format(summary.pending)} ungeprüft`,
    corrected > 0 && `${numberFormat.format(corrected)} ${corrected === 1 ? 'Korrektur' : 'Korrekturen'}`,
    (s.UNSURE ?? 0) > 0 && `${numberFormat.format(s.UNSURE ?? 0)} unsicher`,
    (s.NOT_FOUND ?? 0) > 0 && `${numberFormat.format(s.NOT_FOUND ?? 0)} nicht gefunden`,
    (s.CONFIRMED ?? 0) > 0 && `${numberFormat.format(s.CONFIRMED ?? 0)} wie erfasst bestätigt`,
  ].filter(Boolean);
  return (
    <section className="card address-checks" aria-label="Google-Adressprüfung">
      <div className="address-checks-head">
        <strong>Google-Adressprüfung</strong>
        {parts.length > 0 && <span className="muted">{parts.join(' · ')}</span>}
      </div>
      <p className="muted">
        Google-Vorschläge übernehmen Sie selbst – einzeln im Fall oder gesammelt nach Durchsicht. Nicht übernommene Ergebnisse
        werden nach 29 Tagen gelöscht.
      </p>
      <div className="form-actions">
        {summary.pending > 0 && (
          <button type="button" className="btn" disabled={start.isPending || running} onClick={() => start.mutate()}>
            {running ? 'Google prüft …' : `Mit Google prüfen (${numberFormat.format(summary.pending)})`}
          </button>
        )}
        {reviewable > 0 && (
          <button type="button" className="btn btn-primary" onClick={() => setOpen(true)}>
            Vorschläge durchsehen und übernehmen ({numberFormat.format(reviewable)})
          </button>
        )}
      </div>
      {start.isError && <MutationError error={start.error} />}
      <GoogleAttribution />
      {open && <AcceptCorrectionsDialog onClose={() => setOpen(false)} />}
    </section>
  );
}

// AcceptCorrectionsDialog: alle Vorschläge zur Durchsicht – sichere Korrekturen (Hausnummer von Google bestätigt) sind
// vorausgewählt, teilweise gefundene nicht; übernommen werden die angehakten.
function AcceptCorrectionsDialog({ onClose }: { onClose: () => void }) {
  const { project } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const list = useQuery({
    queryKey: [...projectKeys.review(project.id), 'address-checks', 'all'],
    queryFn: ({ signal }) => papi.addressChecks(undefined, signal),
  });
  const items = (list.data?.items ?? []).filter(
    (c) => ((c.status === 'CORRECTED' || c.status === 'UNSURE') && c.changes.length > 0) || (c.status === 'CONFIRMED' && c.assumed),
  );
  const safe = items.filter((c) => c.status === 'CORRECTED');
  const confirmed = items.filter((c) => c.status === 'CONFIRMED');
  const partial = items.filter((c) => c.status === 'UNSURE');
  const current = picked ?? new Set([...safe, ...confirmed].map((c) => c.lead_id));
  const selected = items.filter((c) => current.has(c.lead_id)).map((c) => c.lead_id);
  const accept = useMutation({
    mutationFn: () => papi.acceptAddressChecks(selected),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: projectKeys.all(project.id) }),
  });
  const toggle = (id: string) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPicked(next);
  };
  // Ganze Gruppe an- bzw. abwählen (z. B. alle Teiltreffer nach Durchsicht übernehmen).
  const toggleGroup = (rows: typeof items, on: boolean) => {
    const next = new Set(current);
    for (const c of rows) {
      if (on) next.add(c.lead_id);
      else next.delete(c.lead_id);
    }
    setPicked(next);
  };
  const group = (title: string, rows: typeof items) => {
    if (rows.length === 0) return null;
    const all = rows.every((c) => current.has(c.lead_id));
    return (
      <section aria-label={title}>
        <div className="address-check-group">
          <h3>
            {title} ({numberFormat.format(rows.length)})
          </h3>
          <button type="button" className="btn btn-small btn-ghost" onClick={() => toggleGroup(rows, !all)}>
            {all ? 'Alle abwählen' : 'Alle auswählen'}
          </button>
        </div>
        <ul className="address-check-list">
          {rows.map((c) => {
            const shown = c.changes.length > 0 ? c.suggestion : c.input;
            const line = `${[shown.street, shown.house_number].filter(Boolean).join(' ')}, ${shown.postal_code} ${shown.city}`;
            return (
              <li key={c.lead_id}>
                <label>
                  <input type="checkbox" checked={current.has(c.lead_id)} onChange={() => toggle(c.lead_id)} />
                  <span>
                    <strong>{c.name}</strong>
                    {c.changes.length > 0 ? <Changes changes={c.changes} /> : <span className="block muted">{line}</span>}
                  </span>
                </label>
                <MapsLink address={line} label="In Google Maps" />
              </li>
            );
          })}
        </ul>
      </section>
    );
  };
  const done = accept.data;
  return (
    <Modal
      title="Google-Vorschläge übernehmen"
      size="wide"
      onClose={onClose}
      actions={
        done ? (
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Schließen
          </button>
        ) : (
          <>
            <button type="button" className="btn" onClick={onClose}>
              Abbrechen
            </button>
            <button type="button" className="btn btn-primary" disabled={selected.length === 0 || accept.isPending} onClick={() => accept.mutate()}>
              {accept.isPending ? 'Übernimmt …' : `Ausgewählte übernehmen (${numberFormat.format(selected.length)})`}
            </button>
          </>
        )
      }
    >
      {done ? (
        <p role="status">
          {numberFormat.format(done.accepted)} übernommen, davon {numberFormat.format(done.resolved)} jetzt eindeutig zugeordnet
          {done.skipped > 0 && ` · ${numberFormat.format(done.skipped)} übersprungen (Adresse inzwischen geändert)`}
          {done.failed > 0 && ` · ${numberFormat.format(done.failed)} nicht möglich`}.
        </p>
      ) : list.isPending ? (
        <PageLoader label="Korrekturen werden geladen …" />
      ) : list.isError ? (
        <ErrorMessage error={list.error} onRetry={() => void list.refetch()} />
      ) : (
        <>
          <p className="muted">Bitte kurz durchsehen – übernommen werden nur die angehakten Vorschläge, jeweils mit Eintrag in der Historie.</p>
          {group('Sicher: Hausnummer von Google bestätigt', safe)}
          {group('Wahrscheinliche Zuordnung von Google bestätigt', confirmed)}
          {group('Nur teilweise gefunden – bitte abwägen', partial)}
          {items.length === 0 && <p>Keine Vorschläge mit Änderungen.</p>}
          {accept.isError && <MutationError error={accept.error} />}
          <GoogleAttribution />
        </>
      )}
    </Modal>
  );
}

const statusText: Record<string, { label: string; badge: string }> = {
  CORRECTED: { label: 'Google schlägt eine Korrektur vor', badge: 'badge-warn' },
  UNSURE: { label: 'Google: nur teilweise gefunden – bitte prüfen', badge: 'badge-warn' },
  NOT_FOUND: { label: 'Google findet die Adresse nicht', badge: 'badge-error' },
  CONFIRMED: { label: 'Google bestätigt die Adresse wie erfasst', badge: 'badge-ok' },
};

// AddressCheckSuggestion: Vorschlag im einzelnen Klärungsfall, per Klick übernehmen.
export function AddressCheckSuggestion({ item }: { item: GeoReviewItem }) {
  const { project } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const accept = useMutation({
    mutationFn: () => papi.acceptAddressChecks([item.id]),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: projectKeys.all(project.id) }),
  });
  const check = item.address_check;
  if (!check) return null;
  const text = statusText[check.status] ?? statusText.UNSURE!;
  // Wahrscheinlich zugeordnet und von Google wie erfasst bestätigt: Übernehmen bestätigt die Zuordnung.
  const confirms = check.status === 'CONFIRMED' && check.changes.length === 0 && isAssumed(item);
  return (
    <div className="address-check-result" aria-label="Google-Vorschlag">
      <span className={`badge ${text.badge}`}>{text.label}</span>{' '}
      {check.suggestion.formatted_address && <span>{check.suggestion.formatted_address}</span>}
      {check.changes.length > 0 && <Changes changes={check.changes} />}
      {check.suggestion.formatted_address && <MapsLink address={check.suggestion.formatted_address} />}
      {accept.data ? (
        <p role="status">
          {confirms ? 'Bestätigt.' : accept.data.resolved > 0 ? 'Übernommen – jetzt eindeutig zugeordnet.' : 'Übernommen – bitte weiter klären.'}
        </p>
      ) : (
        check.status !== 'NOT_FOUND' &&
        (check.changes.length > 0 || confirms) && (
          <button type="button" className="btn btn-small btn-primary" disabled={accept.isPending} onClick={() => accept.mutate()}>
            {confirms ? 'Zuordnung bestätigen' : 'Vorschlag übernehmen'}
          </button>
        )
      )}
      {accept.isError && <MutationError error={accept.error} />}
      <GoogleAttribution />
    </div>
  );
}
